import type {
  WorkflowCaptureField,
  WorkflowCaptureForm,
} from '@app/shared/workflow-commands/workflow-capture-form';
import { resolveWorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import { isWorkflowFormLookupBinding } from '@app/shared/workflow-commands/workflow-form-lookup';

export const captureFieldTypes = [
  { code: 'text', label: 'Short text' },
  { code: 'textarea', label: 'Paragraph' },
  { code: 'number', label: 'Number' },
  { code: 'integer', label: 'Whole number' },
  { code: 'date', label: 'Date' },
  { code: 'boolean', label: 'Yes / no' },
  { code: 'select', label: 'Dropdown' },
] as const;
const ownRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const validId = (value: string) =>
  value.length <= 80 && /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(value);

export function isUnsupportedCaptureForm(value: unknown): boolean {
  return (
    value != null &&
    (!ownRecord(value) ||
      ![1, 2, 3].includes(value['version'] as number) ||
      typeof value['id'] !== 'string' ||
      typeof value['title'] !== 'string' ||
      !Array.isArray(value['fields']) ||
      value['fields'].some(
        (field) =>
          !ownRecord(field) ||
          typeof field['id'] !== 'string' ||
          typeof field['label'] !== 'string' ||
          typeof field['type'] !== 'string' ||
          (field['required'] != null &&
            typeof field['required'] !== 'boolean') ||
          (field['lookup'] != null && (value['version'] !== 3 || field['type'] !== 'select'
            || field['options'] != null || !isWorkflowFormLookupBinding(field['lookup']))) ||
          (field['options'] != null &&
            (!Array.isArray(field['options']) ||
              field['options'].some(
                (option) =>
                  !ownRecord(option) ||
                  typeof option['value'] !== 'string' ||
                  typeof option['label'] !== 'string',
              ))) ||
          (field['visibleWhen'] != null &&
            (!ownRecord(field['visibleWhen']) ||
              typeof field['visibleWhen']['fieldId'] !== 'string')),
      ) ||
      new Set(value['fields'].map((field) => field.id)).size !==
        value['fields'].length ||
      !resolveWorkflowFormLayout(value['version'] as number, value['fields'].map(field => ({
        control: field.type === 'boolean' ? 'checkbox' : field.type, layout: field.layout,
      }))))
  );
}

export interface CaptureFormIssues {
  form: string;
  fields: Record<string, string>;
}
/** Authoring diagnostics only; publication and submission validation remain authoritative. */
export function captureFormIssues(
  form: WorkflowCaptureForm | null | undefined,
): CaptureFormIssues {
  if (!form) return { form: 'Create a form before adding fields.', fields: {} };
  if (isUnsupportedCaptureForm(form))
    return {
      form: 'This saved form is malformed or uses an unsupported version. Its content is preserved.',
      fields: {},
    };
  const formIssue = !validId(form.id)
    ? 'The saved form identifier is invalid. Restore a valid draft before publishing.'
    : !form.title.trim() || form.title.length > 120
      ? 'Enter a form title of 1–120 characters.'
      : !form.fields.length || form.fields.length > 50
        ? 'Add between 1 and 50 fields before publishing.'
        : '';
  return {
    form: formIssue,
    fields: Object.fromEntries(
      form.fields
        .map((field) => [field.id, fieldIssue(form, field)])
        .filter(([, message]) => message),
    ),
  };
}

function fieldIssue(
  form: WorkflowCaptureForm,
  field: WorkflowCaptureField,
): string {
  if (!validId(field.id) || field.id === 'constructor')
    return 'This field has an invalid or reserved saved identifier.';
  if (!captureFieldTypes.some((type) => type.code === field.type))
    return 'This field type is unavailable. Its settings are preserved.';
  if (!field.label.trim() || field.label.length > 120)
    return 'Enter a field label of 1–120 characters.';
  if (
    field.maxLength != null &&
    (!['text', 'textarea'].includes(field.type) ||
      !Number.isInteger(field.maxLength) ||
      field.maxLength < 1 ||
      field.maxLength > 100000)
  )
    return 'Text limits must be whole numbers from 1 to 100000.';
  if (field.minimum != null || field.maximum != null) {
    if (
      !['number', 'integer'].includes(field.type) ||
      [field.minimum, field.maximum].some(
        (value) =>
          value != null &&
          (!Number.isFinite(value) ||
            Math.abs(value) > 1e15 ||
            (field.type === 'integer' && !Number.isInteger(value))),
      )
    )
      return 'Numeric limits must be numbers between −1000000000000000 and 1000000000000000; whole-number fields need whole-number limits.';
    if (
      field.minimum != null &&
      field.maximum != null &&
      field.minimum > field.maximum
    )
      return 'Minimum cannot be greater than maximum.';
  }
  if (field.type !== 'select' && field.options != null)
    return 'Only dropdown fields can contain choices. Change the input type to replace incompatible settings.';
  if (field.type === 'select') {
    if (field.lookup != null) {
      if (form.version !== 3 || !isWorkflowFormLookupBinding(field.lookup) || field.options != null)
        return 'This saved lookup source is unsupported. Its settings are preserved.';
    } else {
    if (!(field.options ?? []).length || (field.options ?? []).length > 100)
      return 'Add between 1 and 100 choices.';
    if (
      (field.options ?? []).some(
        (option) =>
          !option.value.trim() ||
          option.value.length > 120 ||
          !option.label.trim() ||
          option.label.length > 120,
      )
    )
      return 'Each choice needs a stored value and label of 1–120 characters.';
    if (
      new Set((field.options ?? []).map((option) => option.value)).size !==
      (field.options ?? []).length
    )
      return 'Choice values must be unique.';
    }
  }
  if (field.visibleWhen) {
    const controller = form.fields
        .slice(
          0,
          form.fields.findIndex((item) => item.id === field.id),
        )
        .find(
          (item) =>
            item.id === field.visibleWhen!.fieldId &&
            captureFieldTypes.some((type) => type.code === item.type),
        ),
      value = field.visibleWhen.equals;
    if (!controller) return 'Choose an earlier field to control visibility.';
    if (controller.type === 'boolean' && typeof value !== 'boolean')
      return 'Choose Yes or No for the visibility condition.';
    if (
      ['number', 'integer'].includes(controller.type) &&
      (typeof value !== 'number' ||
        !Number.isFinite(value) ||
        Math.abs(value) > 1e15 ||
        (controller.type === 'integer' && !Number.isInteger(value)) ||
        (controller.minimum != null && value < controller.minimum) ||
        (controller.maximum != null && value > controller.maximum))
    )
      return 'Enter a valid controlling-field number for visibility.';
    if (
      ['text', 'textarea', 'date', 'select'].includes(controller.type) &&
      typeof value !== 'string'
    )
      return 'Enter a matching answer for visibility.';
    if (
      controller.type === 'select' &&
      controller.lookup == null &&
      !(controller.options ?? []).some((option) => option.value === value)
    )
      return 'Choose an existing dropdown value for visibility.';
    if (controller.type === 'select' && controller.lookup != null &&
      (typeof value !== 'string' || !value.trim() || value !== value.trim() || value.length > 120))
      return 'Use a stored lookup code of 1–120 characters for visibility.';
    if (
      controller.type === 'date' &&
      (typeof value !== 'string' ||
        value.startsWith('0000-') ||
        !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        !Number.isFinite(Date.parse(value)) ||
        new Date(value).toISOString().slice(0, 10) !== value)
    )
      return 'Choose a valid date for visibility.';
    if (
      ['text', 'textarea'].includes(controller.type) &&
      typeof value === 'string' &&
      !value.trim()
    )
      return 'Enter a nonblank matching answer for visibility.';
    if (
      ['text', 'textarea'].includes(controller.type) &&
      typeof value === 'string' &&
      value.length > (controller.maxLength ?? 4000)
    )
      return 'The matching answer exceeds the controlling field’s text limit.';
  }
  return '';
}
